import { buildQuery } from '@calendly/http-tools'
import { I18n } from '@calendly/i18n'
import { Button } from '@calendly/ui/components/button'
import { AiSparkleIcon } from '@calendly/ui/components/icons'
import { Tooltip } from '@calendly/ui/components/tooltip'
import { useHistory } from 'react-router-dom'

import { useGetUserQuery } from 'common'
import { useNotetakerEntitled } from 'features/meeting_intelligence'
import { TRACKING_EVENT_NAMES, useTrack } from 'hooks/analytics_events'
import { useHomeTabs } from 'hooks/home/tabs'
import { useFetchTrackedMeetingByForeignIdQuery } from 'hooks/tracked_meetings/api'

import { LinkWrapper } from '../../link_wrapper'

const VARIANT = {
  primary: 'primary',
  outline: 'outline',
  secondaryOutline: 'secondary-outline'
}

const MEETING_INCOMPLETE_STATUSES = Object.freeze(['Scheduled', 'PreJoin', 'MeetingInProgress'])
const MEETING_INTELLIGENCE_IN_PROGRESS_STATUSES = Object.freeze(['MeetingEnded', 'Summarizing'])
const MEETING_INTELLIGENCE_ERROR_STATUSES = Object.freeze(['Error', 'Cancelled', 'NoAttendees', 'EmptyTranscript', 'NotEnoughContent'])

export const AiRecapButton = ({ event, decoration = VARIANT.primary, location }) => {
  const history = useHistory()
  const { getTabPathByName } = useHomeTabs()
  const returnTo = getTabPathByName('meetings')
  const { isEntitled: isNotetakerEntitled } = useNotetakerEntitled()
  const { data: meetingIntelligence } = useFetchTrackedMeetingByForeignIdQuery(
    event?.uuid,
    { skip: !event || !isNotetakerEntitled }
  )
  const { data: currentUser } = useGetUserQuery()
  const track = useTrack()

  const { enabled, status, trackedMeetingUuid, deleted, error, trackedDataError } = meetingIntelligence || {}

  if (!enabled || MEETING_INCOMPLETE_STATUSES.includes(status) || MEETING_INTELLIGENCE_ERROR_STATUSES.includes(status)) return null
  if (deleted || error || trackedDataError || event.cancelled) return null

  const url = `/app/tracked_meetings/${trackedMeetingUuid}?${buildQuery({ returnTo })}`

  const segmentProperties = {
    cta_destination: 'meeting_recap',
    cta_key: location,
    cta_text: 'view_recap',
    action: 'view_recap',
    event_uuid: event.uuid,
    mi_event_uuid: trackedMeetingUuid
  }

  const handleClick = (e) => {
    track({
      name: TRACKING_EVENT_NAMES.CTA_CLICKED,
      entities: ['attribution', 'organization', 'user'],
      properties: segmentProperties
    })

    e.preventDefault()
    e.stopPropagation()
    history.push(url)
  }

  if (MEETING_INTELLIGENCE_IN_PROGRESS_STATUSES.includes(status)) {
    return (
      <Button
        size="small"
        decoration={decoration}
        disabled
      >
        {decoration === VARIANT.primary && <AiSparkleIcon />}
        {I18n.t('publisher.meeting_intelligence.scheduled_event.in_progress')}
      </Button>
    )
  }

  let tooltipText = ''

  if (currentUser?.is_pretending) {
    tooltipText = I18n.t('publisher.meeting_intelligence.scheduled_event.pretend_mode')
  }

  return (
    <Tooltip text={tooltipText}>
      <LinkWrapper
        href={url}
        onClick={handleClick}
        disabled={currentUser?.is_pretending}
      >
        <Button
          size="small"
          decoration={decoration}
          data-calendly-label="view-ai-recap-button"
          disabled={currentUser?.is_pretending}
        >
          {decoration === VARIANT.primary && <AiSparkleIcon />}
          {I18n.t('publisher.meeting_intelligence.scheduled_event.view_recap')}
        </Button>
      </LinkWrapper>
    </Tooltip>

  )
}
